import { useEffect, useState } from "react";
import { api, API_BASE, getToken, extractError } from "@/lib/api";
import { useBrands } from "@/contexts/BrandContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScoreBadge } from "@/components/app/ScoreBadge";
import { InfoTip } from "@/components/app/InfoTip";
import {
  Target, Download, ChevronDown, ChevronUp, Sparkles, Star,
  TrendingUp, ShoppingCart, AlertCircle, Search,
} from "lucide-react";
import { toast } from "sonner";

const TIER_META = {
  exact: { label: "Exact", tone: "emerald", desc: "Specific intent, highest ROAS" },
  phrase: { label: "Phrase", tone: "blue", desc: "Variants with same word order" },
  broad: { label: "Broad", tone: "amber", desc: "Discovery — wider net" },
  auto: { label: "Auto", tone: "violet", desc: "Algorithmic targeting" },
};

export default function Strategy() {
  const { selectedBrandId, selectedBrand } = useBrands();
  const [data, setData] = useState({ items: [], upload_id: null, brand_target_acos: null, brand_bid_ranges: {}, negative_keywords: [] });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState({}); // { product_id: true }

  const load = () => {
    if (!selectedBrandId) return;
    setLoading(true);
    api.get(`/recommendations/strategy?brand_id=${selectedBrandId}`)
      .then((r) => setData(r.data || { items: [] }))
      .catch((e) => toast.error(extractError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [selectedBrandId]);

  const filtered = data.items.filter((it) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (it.product.product_name || "").toLowerCase().includes(q)
      || (it.product.sku || "").toLowerCase().includes(q)
      || (it.product.asin || "").toLowerCase().includes(q);
  });

  const exportBulk = () => {
    if (!selectedBrandId) return;
    fetch(`${API_BASE}/recommendations/strategy/export?brand_id=${selectedBrandId}`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    }).then(async (r) => {
      if (!r.ok) throw new Error("Export failed");
      const cd = r.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^";]+)"?/);
      const fname = m ? m[1] : "ads_strategy.csv";
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = fname;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Bulk-upload CSV downloaded — open Amazon Campaign Manager → Bulk Operations");
    }).catch(() => toast.error("Export failed"));
  };

  const totalAddressable = data.items.reduce((s, it) => s + (it.metrics?.total_estimated_demand || 0), 0);
  const totalHighOpp = data.items.reduce((s, it) => s + (it.metrics?.high_opp_terms || 0), 0);

  return (
    <div className="space-y-6" data-testid="strategy-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400 inline-flex items-center gap-1">
            <Target className="h-3 w-3" /> Ads Strategy
          </p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Per-product ad playbook</h1>
          <p className="mt-1 text-sm text-slate-500">
            Auto-generated bidding tiers, target keywords and listing tweaks for every active SKU. Export the full plan as an Amazon Campaign Manager bulk-upload CSV.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
            <Input
              data-testid="strategy-search"
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Filter by product / SKU / ASIN…"
              className="pl-9 h-9 w-72"
            />
          </div>
          <Button
            data-testid="strategy-export-btn"
            onClick={exportBulk}
            disabled={!data.items.length}
            className="bg-slate-900 text-white hover:bg-slate-800 gap-1.5 h-9"
          >
            <Download className="h-3.5 w-3.5" /> Export bulk CSV
          </Button>
        </div>
      </div>

      {/* Brand-level snapshot */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <SummaryCard icon={ShoppingCart} label="Active SKUs" value={data.items.length} sub="with strategy generated" />
        <SummaryCard icon={Sparkles} tone="emerald" label="High-opp terms" value={totalHighOpp.toLocaleString()} sub="≥70 opportunity score" />
        <SummaryCard icon={TrendingUp} tone="blue" label="Addressable demand" value={totalAddressable.toLocaleString()} sub="weekly est. impressions" />
        <SummaryCard icon={AlertCircle} tone="rose" label="Negative keywords" value={(data.negative_keywords || []).length} sub="auto-detected out of scope" />
      </div>

      {!selectedBrandId && (
        <div className="rounded-xl border border-slate-200 bg-white p-12 text-center text-sm text-slate-500">Select a brand to load the ads strategy.</div>
      )}
      {selectedBrandId && loading && (
        <div className="rounded-xl border border-slate-200 bg-white p-12 text-center text-sm text-slate-500">Generating strategy…</div>
      )}
      {selectedBrandId && !loading && !data.items.length && (
        <div className="rounded-xl border border-slate-200 bg-white p-12 text-center text-sm text-slate-500">
          <Target className="mx-auto h-8 w-8 text-slate-400" />
          <div className="mt-3 font-semibold text-slate-700">No strategy yet</div>
          <p className="mt-1 text-xs text-slate-500">Add active products and process at least one weekly upload to populate this page.</p>
        </div>
      )}

      <div className="space-y-3" data-testid="strategy-products">
        {filtered.map((it) => {
          const open = !!expanded[it.product.id];
          return (
            <div key={it.product.id} className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
              <button
                data-testid={`strategy-product-${it.product.sku || it.product.id}`}
                onClick={() => setExpanded((s) => ({ ...s, [it.product.id]: !s[it.product.id] }))}
                className="w-full px-5 py-4 flex items-center gap-4 hover:bg-slate-50/50 transition-colors text-left"
              >
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-slate-900 flex items-center gap-2 truncate">
                    {it.product.is_hero_sku && <Star className="h-3.5 w-3.5 text-amber-500 fill-amber-400 shrink-0" />}
                    <span className="truncate">{it.product.product_name}</span>
                  </div>
                  <div className="mt-0.5 text-[11px] font-mono text-slate-500 truncate">
                    {it.product.sku} {it.product.asin ? `· ${it.product.asin}` : ""} · {it.product.lifecycle_stage} · {it.product.inventory_status}
                  </div>
                </div>
                <div className="flex items-center gap-5 shrink-0 text-xs">
                  <Stat label="Matched terms" value={it.metrics.matched_terms} />
                  <Stat label="High-opp" value={it.metrics.high_opp_terms} tone="emerald" />
                  <Stat label="Bid actions" value={it.metrics.bidding_actions} tone="blue" />
                  <Stat label="Listing tweaks" value={it.metrics.listing_suggestions} tone="amber" />
                </div>
                {open ? <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" /> : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />}
              </button>

              {open && (
                <div className="border-t border-slate-100 p-5 space-y-5 bg-slate-50/30">
                  {/* Tier bidding */}
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1">
                      Campaign tiers <InfoTip term="MATCH_TYPE" title="Match type" text="Exact = specific intent, highest ROAS. Phrase = variants. Broad = discovery. Auto = algorithmic." />
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                      {Object.entries(TIER_META).map(([key, meta]) => {
                        const acts = it.tiers[key] || [];
                        if (!acts.length) return null;
                        return (
                          <div key={key} data-testid={`strategy-tier-${it.product.id}-${key}`} className={`rounded-lg border bg-white border-slate-200 overflow-hidden`}>
                            <div className={`px-3 py-2 flex items-center justify-between border-b border-slate-100 bg-${meta.tone}-50/40`}>
                              <div>
                                <div className="text-xs font-bold uppercase tracking-wider text-slate-700">{meta.label} match</div>
                                <div className="text-[10px] text-slate-500">{meta.desc}</div>
                              </div>
                              <div className="text-[10px] text-slate-500">{acts.length} keywords</div>
                            </div>
                            <table className="w-full text-xs">
                              <thead className="bg-white text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                                <tr>
                                  <th className="text-left px-3 py-1.5">Keyword</th>
                                  <th className="text-right px-3 py-1.5">Bid (₹)</th>
                                  <th className="text-right px-3 py-1.5">Opp</th>
                                </tr>
                              </thead>
                              <tbody>
                                {acts.slice(0, 8).map((a) => {
                                  const lo = a.suggested_bid_low ?? 0;
                                  const hi = a.suggested_bid_high ?? 0;
                                  const bidLabel = (lo === 0 && hi === 0) ? "—" : (lo === hi ? `₹${lo}` : `₹${lo}–${hi}`);
                                  return (
                                    <tr key={a.id} className="border-b border-slate-50 last:border-0">
                                      <td className="px-3 py-1.5 text-slate-800 truncate max-w-[18rem]" title={a.keyword || ""}>
                                        {a.keyword || <span className="text-slate-300">(unmapped)</span>}
                                      </td>
                                      <td className="px-3 py-1.5 text-right font-mono text-slate-900">{bidLabel}</td>
                                      <td className="px-3 py-1.5 text-right"><ScoreBadge score={a.opportunity_score || 0} size="xs" /></td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                            {acts.length > 8 && (
                              <div className="px-3 py-1.5 text-[10px] text-slate-400 bg-white">+{acts.length - 8} more in export</div>
                            )}
                          </div>
                        );
                      })}
                      {!Object.values(it.tiers).some((arr) => arr.length) && (
                        <div className="col-span-2 rounded-lg border border-dashed border-slate-300 p-4 text-xs text-slate-500 text-center">
                          No bidding actions for this SKU. Run "Save & Regenerate" on the Settings page or upload a fresh week of data.
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Top matched terms */}
                  {!!it.top_terms?.length && (
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Top matched search terms</div>
                      <div className="flex flex-wrap gap-1.5">
                        {it.top_terms.slice(0, 12).map((t) => (
                          <span key={t.id} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px]">
                            <span className="text-slate-700">{t.search_term}</span>
                            <ScoreBadge score={t.opportunity_score || 0} size="xs" />
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Listing suggestions */}
                  {!!it.listing_suggestions?.length && (
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Listing optimization tweaks</div>
                      <ul className="space-y-1.5">
                        {it.listing_suggestions.map((l) => (
                          <li key={l.id} className="rounded-lg border border-slate-200 bg-white p-2.5 text-xs">
                            <div className="flex items-center gap-2">
                              <span className="inline-flex items-center rounded-full border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border-slate-200 text-slate-700 bg-slate-50">{l.suggestion_type}</span>
                              <span className="text-slate-500 text-[10px]">{l.priority || ""}</span>
                            </div>
                            <div className="mt-1 text-slate-700">{l.suggested_title || l.suggested_bullet || l.suggested_change || l.rationale}</div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Negatives */}
      {!!data.negative_keywords?.length && (
        <div className="bg-white border border-rose-200 rounded-xl shadow-sm" data-testid="strategy-negatives">
          <div className="px-5 py-4 border-b border-rose-100 flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-rose-600" />
            <div>
              <div className="text-base font-semibold text-slate-900">Negative keywords ({data.negative_keywords.length})</div>
              <div className="text-xs text-slate-500">Out-of-scope terms to add as negative-exact across all campaigns. Already included in the bulk-export CSV.</div>
            </div>
          </div>
          <div className="px-5 py-4 flex flex-wrap gap-1.5 max-h-48 overflow-y-auto">
            {data.negative_keywords.slice(0, 200).map((kw, i) => (
              <span key={i} className="inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-2.5 py-0.5 text-[11px] text-rose-800">{kw}</span>
            ))}
            {data.negative_keywords.length > 200 && <span className="text-[11px] text-rose-700 font-semibold">+{data.negative_keywords.length - 200} more in export</span>}
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, sub, tone = "slate" }) {
  const tones = { slate: "text-slate-700", emerald: "text-emerald-700", blue: "text-blue-700", rose: "text-rose-700", amber: "text-amber-700", violet: "text-violet-700" };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
        <Icon className={`h-3.5 w-3.5 ${tones[tone]}`} />
      </div>
      <div className={`mt-1 text-2xl font-bold tracking-tight ${tones[tone]}`}>{value}</div>
      <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}

function Stat({ label, value, tone = "slate" }) {
  const tones = { slate: "text-slate-700", emerald: "text-emerald-700", blue: "text-blue-700", amber: "text-amber-700" };
  return (
    <div className="text-right">
      <div className="text-[10px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className={`text-base font-bold ${tones[tone]}`}>{value}</div>
    </div>
  );
}
